import React from 'react'

import { useNavigate } from 'react-router-dom';
import { urlBase64ToUint8Array } from '../utils/shared.js';
// c:\xampp\htdocs\miners-market-main\components\NotificationBell.js

export default function NotificationBell({ user, onShowLogin, className }) {
    const navigate = useNavigate();
    const [notifications, setNotifications] = React.useState([]);
    const [isOpen, setIsOpen] = React.useState(false);
    const [isSubscribed, setIsSubscribed] = React.useState(false);
    const [isToggling, setIsToggling] = React.useState(false);
    const [isSupported, setIsSupported] = React.useState(true);
    const dropdownRef = React.useRef(null);

    const unreadCount = notifications.filter(n => !n.is_read).length;

    const fetchNotifications = React.useCallback(async () => {
        if (!user) return;
        try {
            const response = await fetch('/api/get_user_notifications.php', { credentials: 'include' });
            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        }
    }, [user]);

    React.useEffect(() => {
        // Feature detect: Check if Notifications are supported in this environment
        if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
            setIsSupported(false);
            return;
        }

        // Check subscription status when menu opens
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            navigator.serviceWorker.ready.then(registration => {
                registration.pushManager.getSubscription().then(subscription => {
                    setIsSubscribed(!!subscription);
                });
            });
        }
    }, [isOpen]);

    React.useEffect(() => {
        fetchNotifications();
        // Optional: Poll for new notifications every few minutes
        const interval = setInterval(fetchNotifications, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleToggleSubscription = async () => {
        setIsToggling(true);
        try {
            // FIX: Check if Notification API is available
            if (!('Notification' in window)) {
                throw new Error("Push notifications are not supported in this app version.");
            }

            // Add a timeout to prevent the button from hanging indefinitely if SW isn't ready
            const registrationPromise = navigator.serviceWorker.ready;
            const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Service Worker not ready. Try refreshing.")), 5000));
            
            const registration = await Promise.race([registrationPromise, timeoutPromise]);
            
            if (isSubscribed) {
                // Unsubscribe
                const subscription = await registration.pushManager.getSubscription();
                if (subscription) {
                    await subscription.unsubscribe();
                }
                setIsSubscribed(false);
                alert("Notifications disabled.");
            } else {
                // Subscribe
                // Explicitly request permission
                const permission = await Notification.requestPermission();
                if (permission !== 'granted') {
                    throw new Error("Permission denied. Please enable notifications in settings.");
                }

                const configResponse = await fetch('/api/get_config.php?keys=vapid');
                const config = await configResponse.json();
                const vapidPublicKey = config.VAPID.publicKey;
                
                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
                });
                
                await fetch('/api/save_subscription.php', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(subscription)
                });
                setIsSubscribed(true);
                alert("Notifications enabled!");
            }
        } catch (error) {
            console.error('Subscription toggle failed:', error);
            alert("Error: " + error.message);
        } finally {
            setIsToggling(false);
        }
    };

    const handleNotificationClick = async (notification) => {
        setIsOpen(false);
        navigate(notification.url);
        if (!notification.is_read) {
            // Mark as read on the backend
            await fetch('/api/mark_notifications_read.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ids: [notification.id] })
            });
            // Update state locally for immediate UI feedback
            setNotifications(prev => prev.map(n => n.id === notification.id ? { ...n, is_read: 1 } : n));
        }
    };

    const markAllAsRead = async () => {
        const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
        if (unreadIds.length === 0) return;

        await fetch('/api/mark_notifications_read.php', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: unreadIds })
        });
        setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    };

    const handleButtonClick = () => {
        if (!user) {
            if (onShowLogin) onShowLogin();
            return;
        }
        setIsOpen(!isOpen);
    };

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            <button onClick={handleButtonClick} className={`relative p-2 ${className || 'text-gray-900'} hover:text-blue-600 focus:outline-none transition-colors`}>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                {unreadCount > 0 && <span className="absolute top-0 right-0 block h-4 w-4 transform -translate-y-1/2 translate-x-1/2 rounded-full text-white bg-red-500 text-xs flex items-center justify-center">{unreadCount}</span>}
            </button>

            {isOpen && (
                <>
                    {/* Mobile backdrop */}
                    <div className="fixed inset-0 bg-black/50 z-[60] md:hidden" onClick={() => setIsOpen(false)} />
                    {/* Mobile full-width panel / Desktop dropdown */}
                    <div className="fixed bottom-[90px] left-4 right-4 w-[calc(100%-1rem)] md:absolute md:right-0 md:top-auto md:bottom-auto md:left-auto md:w-80 rounded-2xl shadow-2xl bg-white ring-1 ring-black ring-opacity-5 z-[70] flex flex-col max-h-[85vh]">
                        <div className="px-4 py-3 border-b flex justify-between items-center bg-gray-50 rounded-t-2xl">
                            <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-600">
                                <i className="fas fa-times text-xl"></i>
                            </button>
                            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Push Notifications</span>
                            <button 
                                onClick={handleToggleSubscription}
                                disabled={isToggling}
                                title={isSubscribed ? "Disable Notifications" : "Enable Notifications"}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isSubscribed ? 'bg-blue-600' : 'bg-gray-300'}`}
                            >
                                <span className={`inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition-transform duration-200 ease-in-out ${isSubscribed ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                        </div>

                        <div className="px-4 py-3 flex justify-between items-center border-b bg-white">
                            <h3 className="text-sm font-medium text-gray-900">Recent</h3>
                            {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs text-blue-600 hover:underline">Mark all as read</button>}
                        </div>
                        <div className="flex-1 overflow-y-auto p-2">
                            {notifications.length === 0 ? (
                                <div className="text-center py-8">
                                    <p className="text-gray-500 text-sm">No notifications yet.</p>
                                    {!isSubscribed && isSupported && <p className="text-xs text-blue-500 mt-2 cursor-pointer hover:underline" onClick={handleToggleSubscription}>Enable push notifications</p>}
                                </div>
                            ) : (
                                notifications.map(n => (
                                    <a key={n.id} onClick={() => handleNotificationClick(n)} className={`block px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer border-b border-gray-50 ${!n.is_read ? 'bg-blue-50' : ''}`}>
                                        <p className={`font-semibold ${!n.is_read ? 'text-gray-900' : 'text-gray-600'}`}>{n.title}</p>
                                        <p className="text-gray-500 text-xs">{n.body}</p>
                                        <p className="text-right text-gray-400 text-xs mt-1">{new Date(n.created_at).toLocaleString()}</p>
                                    </a>
                                ))
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
